import { camelCase, groupBy, upperFirst } from 'es-toolkit';

import { User } from '@paragon/contract-library';

import { PropertyKey } from './propertyMetadata';
import { JobWithUsers } from './store/jobsSlice';
import { DoubleOperator, JobFilter, SingleOperator } from './store/viewsSlice';

export type SingleOperatorFilterFunction<T> = (a: T, b: string) => boolean;
export type DoubleOperatorFilterFunction<T> = (a: T, b: string, c: string) => boolean;

type FilterFunctions<T> = {
  [K in SingleOperator]?: SingleOperatorFilterFunction<T>;
} & {
  [K in DoubleOperator]?: DoubleOperatorFilterFunction<T>;
};

const numberFilterFunctions: FilterFunctions<number> = {
  '>=': (a, b) => a >= parseInt(b, 10),
  '<=': (a, b) => a <= parseInt(b, 10),
  '!=': (a, b) => a !== parseInt(b, 10),
  '>': (a, b) => a > parseInt(b, 10),
  '<': (a, b) => a < parseInt(b, 10),
  '=': (a, b) => a === parseInt(b, 10),
  '>= <=': (a, b, c) => a >= parseInt(b, 10) && a <= parseInt(c, 10),
};

const textFilterFunctions: FilterFunctions<string> = {
  '=': (a, b) => a.includes(b),
  '!=': (a, b) => a !== b,
};

const enumFilterFunctions: FilterFunctions<string> = {
  '=': (a, b) => a === upperFirst(camelCase(b)),
  '!=': (a, b) => a !== b,
};

const assigneeTextFilter: FilterFunctions<readonly User[] | undefined> = {
  '=': (a, b) => {
    const alpha = a;
    const beta = b.replace(/\s+/g, '').toLowerCase();
    return (
      alpha!.find(
        (user) =>
          user.firstName.replace(/\s+/g, '').toLowerCase() + user.lastName.replace(/\s+/g, '').toLowerCase() === beta,
      ) != null
    );
  },
  '!=': (a, b) => {
    const alpha = a;
    const beta = b.replace(/\s+/g, '').toLowerCase();
    return (
      alpha!.find(
        (user) =>
          user.firstName.replace(/\s+/g, '').toLowerCase() + user.lastName.replace(/\s+/g, '').toLowerCase() === beta,
      ) == null
    );
  },
};

const regionTextFilter: FilterFunctions<readonly string[]> = {
  '=': (a, b) => {
    const alpha = a;
    const beta = b.replace(/\s+/g, '').toLowerCase();
    return alpha.find((region) => region.replace(/\s+/g, '').toLowerCase() === beta.toLowerCase()) != null;
  },
  '!=': (a, b) => {
    const alpha = a;
    const beta = b.replace(/\s+/g, '').toLowerCase();
    return alpha.find((region) => region.replace(/\s+/g, '').toLowerCase() === beta.toLowerCase()) == null;
  },
};

const textFilterFunctionsExact: FilterFunctions<string> = {
  '=': (a, b) => {
    const alpha = a.replace(/\s+/g, '');
    const beta = b.replace(/\s+/g, '');
    return alpha.toLowerCase() === beta.toLowerCase();
  },
  '!=': (a, b) => {
    const alpha = a.replace(/\s+/g, '');
    const beta = b.replace(/\s+/g, '');
    return alpha.toLowerCase() !== beta.toLowerCase();
  },
};

const dateFilterFunctions: FilterFunctions<string> = {
  '>=': (a, b) => !isNaN(Date.parse(b)) && new Date(a).setHours(0, 0, 0, 0) >= Date.parse(b),
  '<=': (a, b) => !isNaN(Date.parse(b)) && new Date(a).setHours(0, 0, 0, 0) <= Date.parse(b),
  '!=': (a, b) => !isNaN(Date.parse(b)) && new Date(a).setHours(0, 0, 0, 0) !== Date.parse(b),
  '>': (a, b) => !isNaN(Date.parse(b)) && new Date(a).setHours(0, 0, 0, 0) > Date.parse(b),
  '<': (a, b) => !isNaN(Date.parse(b)) && new Date(a).setHours(0, 0, 0, 0) < Date.parse(b),
  '=': (a, b) => !isNaN(Date.parse(b)) && new Date(a).setHours(0, 0, 0, 0) === Date.parse(b),
  '>= <=': (a, b, c) =>
    !isNaN(Date.parse(b)) &&
    new Date(a).setHours(0, 0, 0, 0) >= Date.parse(b) &&
    !isNaN(Date.parse(c)) &&
    new Date(a).setHours(0, 0, 0, 0) <= Date.parse(c),
};

export const filterFunctionsCollection: { [K in PropertyKey]: FilterFunctions<JobWithUsers[K]> } = {
  id: numberFilterFunctions,
  name: textFilterFunctions,
  regions: regionTextFilter,
  jobType: enumFilterFunctions,
  creator: {
    '=': (a: User | undefined, b: string) =>
      !!a && `${a.firstName} ${a.lastName}`.toLowerCase().includes(b.toLowerCase()),
    '!=': (a: User | undefined, b: string) =>
      !(!!a && `${a.firstName} ${a.lastName}`.toLowerCase().includes(b.toLowerCase())),
  },
  assignedTo: assigneeTextFilter,
  createdTimestamp: dateFilterFunctions,
  numberOfDesigns: numberFilterFunctions,
  openedBy: {
    '=': (a: readonly User[] | undefined, b: string) =>
      a?.some((a) => `${a.firstName} ${a.lastName}`.toLowerCase().includes(b.toLowerCase())) ?? false,
    '!=': (a: readonly User[] | undefined, b: string) =>
      !(a?.some((a) => `${a.firstName} ${a.lastName}`.toLowerCase().includes(b.toLowerCase())) ?? false),
  },
  organizationOwnedBy: textFilterFunctions,
  jobStatus: enumFilterFunctions,
  jobPriority: enumFilterFunctions,
  sharedWithOrganizations: {
    '=': (a: readonly string[], b: string) => a.some((a) => a.toLowerCase().includes(b.toLowerCase())),
    '!=': (a: readonly string[], b: string) => !a.some((a) => a.toLowerCase().includes(b.toLowerCase())),
  },
  billingLocation: textFilterFunctionsExact,
};

function filterJobForIndividualFilter(job: JobWithUsers, jobFilter: JobFilter) {
  const filterFunctions = filterFunctionsCollection[jobFilter.propertyKey];
  if (jobFilter.operator === '>= <=') {
    const filterFunction = filterFunctions[jobFilter.operator];
    if (filterFunction == null) {
      console.error('Filter function not specified for operator.');
      return true;
    }
    return filterFunction(job[jobFilter.propertyKey] as never, jobFilter.low, jobFilter.high);
  }
  const filterFunction = filterFunctions[jobFilter.operator];
  if (filterFunction == null) {
    console.error('Filter function not specified for operator.');
    return true;
  }
  return filterFunction(job[jobFilter.propertyKey] as never, jobFilter.value);
}

export function filterJob(job: JobWithUsers, jobFilters: readonly JobFilter[]) {
  const jobFiltersGroupedByProperty = groupBy(jobFilters, (jobFilter) => jobFilter.propertyKey);
  return Object.values(jobFiltersGroupedByProperty).every((jobFilterForProperty) =>
    jobFilterForProperty.some((jobFilter) => filterJobForIndividualFilter(job, jobFilter)),
  );
}
